import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ProjectMetadataService } from '../../../../shared/project-metadata.service';
import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { TabService } from '../../../../shared/tab.service';
import {
  DEFAULT_FEATHER_PERCENT,
  FEATHER_PERCENT_STEP,
  MAX_FEATHER_PERCENT,
  MIN_FEATHER_PERCENT,
} from '../../pages/preview/spotlight-overlay/spotlight-feather';

/** Card for toggling Power User Mode at the project level (Issue #1228). */
@Component({
  selector: 'app-power-user-mode-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatProgressBarModule,
  ],
  templateUrl: './power-user-mode-card.component.html',
  styleUrl: './power-user-mode-card.component.scss',
})
export class PowerUserModeCardComponent implements OnInit {
  @Input() projectId = '';
  @Input() enabled = false;
  @Output() enabledChange = new EventEmitter<boolean>();

  saving = false;
  error = '';

  /** Spotlight feather as a percentage of the highlighted box size (Issue #1496). */
  highlightFeatherPct = DEFAULT_FEATHER_PERCENT;

  // Exposed for the template so the slider bounds stay in sync with the overlay.
  readonly minFeatherPct = MIN_FEATHER_PERCENT;
  readonly maxFeatherPct = MAX_FEATHER_PERCENT;
  readonly featherPctStep = FEATHER_PERCENT_STEP;

  private projectMetadataService = inject(ProjectMetadataService);
  private tabService = inject(TabService);

  ngOnInit(): void {
    this.highlightFeatherPct = this.tabService.getHighlightFeatherPct();
  }

  onHighlightFeatherChange(value: number): void {
    this.highlightFeatherPct = value;
    this.tabService.saveHighlightFeatherPct(this.projectId, value);
  }

  async onToggle(checked: boolean): Promise<void> {
    if (this.saving) return;

    this.saving = true;
    this.error = '';
    try {
      await this.projectMetadataService.updateProjectMetadata(this.projectId, {
        powerUserMode: checked,
      });

      this.enabled = checked;
      this.enabledChange.emit(checked);

      ClientLoggingService.log('INFO', `[PowerUserModeCard] Power user mode ${checked ? 'enabled' : 'disabled'}`);
    } catch (err) {
      ClientLoggingService.log('ERROR', '[PowerUserModeCard] Failed to save:', err);
      this.error = 'Failed to save power user mode setting.';
    } finally {
      this.saving = false;
    }
  }

  /** Slider tooltip label, e.g. `5%`. */
  formatFeatherLabel(value: number): string {
    return `${value}%`;
  }
}
